import { ConversationInput } from "@mirage/mosaics/Chat/components/chat/ConversationInput";
import { ConversationMessages } from "@mirage/mosaics/Chat/components/chat/ConversationMessages";
import { ConversationMessageSourceChips } from "@mirage/mosaics/Chat/components/chat/ConversationMessageSourceChips";
import { ConversationSuggestedQueries } from "@mirage/mosaics/Chat/components/chat/suggestions/ConversationSuggestedQueries";
import { AddSourcesModal } from "@mirage/mosaics/Chat/components/compose-sources/AddSourcesModal";
import { useComposeSourcesCache } from "@mirage/mosaics/Chat/data/ComposeSourcesCache";
import { WorkflowAgentMessageHeader } from "@mirage/mosaics/ComposeAssistant/components/workflow-agent/WorkflowAgentMessageHeader";
import { postUserMessage } from "@mirage/service-conversation";
import useConversation from "@mirage/service-conversation/hooks/useConversation";
import { useWorkflowAgent } from "@mirage/service-conversation/hooks/useWorkflowAgents";
import { convertToChatMessage } from "@mirage/service-conversation/utils/convertToChatMessage";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import i18n from "@mirage/translations";
import { memo, useCallback, useMemo } from "react";
import styles from "./ConversationChat.module.css";

import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { getDashActionSurfaceComponentLoggingSource } from "@mirage/service-conversation/hooks/useConversationRouterState";
import { useManuallyAddedSources } from "@mirage/service-conversation/hooks/useManuallyAddedSources";
import type {
  AgentId,
  ConversationMessage,
  ModelId,
} from "@mirage/service-conversation/types";
import { convertChatSourceToDocumentInfo } from "@mirage/service-conversation/utils/convertChatSourceToDocumentInfo";
import type { ComposeParams } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/assist_compose_pb";
import type { ReactNode } from "react";
import type { StackChatMetadata } from "../components/stack-chat/useStackChatDetails";

export type UserMessageOptions = {
  skipSearch?: boolean;
  agentIds?: AgentId[];
};

interface ConversationChatProps {
  conversationId: string;
  handleClickAdd: () => void;
  handleCloseAddSourcesModal: () => void;
  isAddSourceModalOpen: boolean;
  variant?: "default" | "condensed";
  selectedModel?: ModelId;
  setSelectedModel: (model: ModelId) => void;
  agentId?: string;
  startingMessageNode?: ReactNode;
  showEmptyChatInstructions?: boolean;
  messages?: ConversationMessage[];
  progressString?: string;
  isWaitingForResponse: boolean;
  agentPayload: ComposeParams;
  stackChatMetadata?: StackChatMetadata | null;
  userMessageOptions?: UserMessageOptions | null;
  setUserMessageOptions: (options: UserMessageOptions | undefined) => void;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
    },
  ) => void;
}

export const ConversationChat = memo(
  ({
    variant = "default",
    conversationId,
    handleClickAdd,
    handleCloseAddSourcesModal,
    isAddSourceModalOpen,
    agentId,
    startingMessageNode,
    showEmptyChatInstructions = false,
    agentPayload,
    messages = [],
    progressString,
    isWaitingForResponse,
    stackChatMetadata,
    userMessageOptions,
    setUserMessageOptions,
    logComposeEvent,
    selectedModel,
    setSelectedModel,
  }: ConversationChatProps) => {
    const conversation = useConversation(conversationId);
    const agent = useWorkflowAgent(agentId);
    const { manuallyAddedSources, setManuallyAddedSources } =
      useManuallyAddedSources();
    const sourcesContentCache = useComposeSourcesCache(manuallyAddedSources);
    const isStackChat = !!stackChatMetadata;

    const startingMessageNodeToDisplay = useMemo(() => {
      if (agent) {
        return <WorkflowAgentMessageHeader agent={agent} />;
      }
      return startingMessageNode;
    }, [agent, startingMessageNode]);

    const composeMessages = useMemo(
      () => messages?.map(convertToChatMessage) || [],
      [messages],
    );

    const attachments = useMemo(
      () => manuallyAddedSources.map(convertChatSourceToDocumentInfo),
      [manuallyAddedSources],
    );
    const handleSubmit = useCallback(
      (text: string) => {
        let skipSearch = false;

        // If the current conversation is a Stack Chat, we want to skip search on the user's first
        // message and pass along the sources from the stack on each turn to ensure they are always used.
        const { hasUserSentFirstMessage, stackSources = [] } =
          stackChatMetadata || {};
        if (isStackChat && !hasUserSentFirstMessage) {
          skipSearch = true;
        } else if (userMessageOptions?.skipSearch) {
          skipSearch = true;
        }

        postUserMessage({
          conversationId,
          message: text,
          model: selectedModel,
          agentPayload: JSON.stringify(agentPayload),
          attachments: [...stackSources, ...attachments],
          skipSearch,
          agentIds: userMessageOptions?.agentIds || [],
          source:
            getDashActionSurfaceComponentLoggingSource("compose_chat_pane"),
        });
        setManuallyAddedSources([]);
        setUserMessageOptions(undefined);
      },
      [
        attachments,
        conversationId,
        selectedModel,
        agentPayload,
        setManuallyAddedSources,
        stackChatMetadata?.hasUserSentFirstMessage,
        stackChatMetadata?.stackSources,
        userMessageOptions?.skipSearch,
        userMessageOptions?.agentIds,
      ],
    );

    const handleClickSuggestedQuery = useCallback(
      (query: string) => {
        handleSubmit(query);
      },
      [handleSubmit],
    );

    const removeSource = useCallback(
      (sourceToRemove: ChatSource) => {
        const sourceUUID = getSourceUUID(sourceToRemove);
        if (!sourceUUID) {
          return;
        }

        setManuallyAddedSources((prevSources) =>
          prevSources.filter((s) => {
            const currentUUID = getSourceUUID(s);
            return currentUUID !== sourceUUID;
          }),
        );
      },
      [setManuallyAddedSources],
    );

    const addSource = useCallback(
      (sourceToAdd: ChatSource) => {
        const uuid = getSourceUUID(sourceToAdd);
        if (!uuid) {
          return; // Skip if no UUID
        }

        setManuallyAddedSources((prevSources) => {
          const sourceUUIDs = new Set(prevSources.map(getSourceUUID));
          if (sourceUUIDs.has(uuid)) {
            return prevSources; // Skip if source already exists
          }
          return [...prevSources, sourceToAdd];
        });
      },
      [setManuallyAddedSources],
    );

    const displayMessages = composeMessages;

    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <ConversationMessages
            conversationId={conversation?.serverConversationId}
            variant={variant}
            messages={displayMessages}
            startingMessageNode={startingMessageNodeToDisplay}
            isWaitingForResponse={isWaitingForResponse}
            progressString={progressString}
            onRemoveSource={removeSource}
            isLastMessageFollowUpSuggestions={false} // do i need this?
            artifacts={[]}
            logComposeEvent={logComposeEvent}
            onOpenArtifact={() => {}}
            renderNewSources={() => (
              <ConversationMessageSourceChips
                sources={manuallyAddedSources}
                removeSource={removeSource}
                logComposeEvent={logComposeEvent}
              />
            )}
            footer={
              conversation?.suggestedQueries &&
              conversation?.suggestedQueries.length > 0 &&
              !isWaitingForResponse && (
                <ConversationSuggestedQueries
                  suggestedQueries={conversation.suggestedQueries}
                  onSubmitText={handleClickSuggestedQuery}
                  logComposeEvent={logComposeEvent}
                  conversationId={conversation?.serverConversationId}
                  assistRequestId={conversation?.assistRequestId}
                />
              )
            }
            fullHeight={showEmptyChatInstructions}
            hideSources={!!stackChatMetadata}
            assistRequestId={conversation?.assistRequestId}
          />
          <ConversationInput
            variant={variant}
            sources={manuallyAddedSources}
            onSubmit={handleSubmit}
            logComposeEvent={logComposeEvent}
            messages={composeMessages}
            placeholderOverride={i18n.t("conversation_prompt_placeholder")}
            handleClickAdd={handleClickAdd}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            // Don't provide the ability to add sources if this is a Stack Chat
            showAddSourcesButton={!stackChatMetadata}
            conversationId={conversation?.serverConversationId}
            assistRequestId={conversation?.assistRequestId}
          />
          {isAddSourceModalOpen && (
            <AddSourcesModal
              sources={manuallyAddedSources}
              addSource={addSource}
              removeSource={removeSource}
              onRequestClose={handleCloseAddSourcesModal}
              logComposeEvent={logComposeEvent}
              sourcesContentCache={sourcesContentCache}
            />
          )}
        </div>
      </div>
    );
  },
);

ConversationChat.displayName = "ConversationChat";
